import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../api/api";
import { SalaryReport } from "../../types/salaryReports.type";
import {
  Button,
  DatePicker,
  Dropdown,
  Image,
  Menu,
  message,
  Popconfirm,
  Space,
  Table,
  Typography,
} from "antd";
import { API } from "../../config/config";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { DownOutlined, PrinterOutlined } from "@ant-design/icons";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function SalaryReports() {
  const { authState } = useAuth();
  const { itemId, name, type } = useParams();
  const [reports, setReports] = useState<SalaryReport[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const handleExport = () => {
    const exportParams = {
      startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
      endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
    };
    downloadFile(
      `/export/salary-reports/${itemId}`,
      "salary_reports_data.xlsx",
      exportParams
    );
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/${type}/reports/${itemId}`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
        },
      });
      setReports(response.data.reports);
      setTotal(response.data.total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение отчетов.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      await api.delete(`/salary/worker/${reportId}/report`);
      message.success("Отчёт успешно удален.");
      fetchReports();
    } catch (error: any) {
      console.error("Error deleting report", error);
      message.error(error.response.data.error);
    }
  };

  const handlePrintReports = () => {
    if (selectedReports.length === 0) {
      message.warning("Пожалуйста, выберите хотя бы один отчет для печати.");
      return;
    }

    const selectedReportsData = reports.filter(report => 
      selectedReports.includes(report._id)
    );

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('ru-RU');
    const periodText = dateRange 
      ? `${dateRange[0].format('DD.MM.YYYY')} - ${dateRange[1].format('DD.MM.YYYY')}`
      : 'Все время';

    // Подсчет итогов
    const totalAmount = selectedReportsData.reduce((sum, report) => sum + (report.totalPrice || 0), 0);
    const totalYuk = selectedReportsData.reduce((sum, report) => sum + (report.yuk || 0), 0);

    const printHtml = `
      <html>
        <head>
          <title>Отчет по зарплате: ${name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              margin: 20px;
              color: #222;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              font-size: 18px;
              font-weight: bold;
            }
            .header .info {
              margin-top: 5px;
              font-size: 12px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              border: 2px solid #000;
            }
            th, td {
              border: 1px solid #000 !important;
              padding: 8px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              text-align: center;
              border: 1px solid #000 !important;
            }
            tr {
              border: 1px solid #000 !important;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @media print {
              th, td {
                border: 1px solid #000 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              tr {
                border: 1px solid #000 !important;
              }
            }
            .summary {
              margin-top: 20px;
              padding: 15px;
              border: 2px solid #1890ff;
              border-radius: 8px;
              background-color: #f0f8ff;
            }
            .summary h3 {
              margin: 0 0 10px 0;
              font-size: 14px;
              color: #1890ff;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
            }
            .summary-item {
              background: white;
              padding: 8px;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
              text-align: center;
            }
            .summary-item strong {
              display: block;
              margin-bottom: 4px;
              color: #333;
            }
            .details {
              font-size: 10px;
              color: #666;
              margin-top: 4px;
            }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .summary { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ОТЧЕТ ПО ЗАРПЛАТЕ: ${name}</h1>
            <div class="info">
              Дата: ${currentDate}<br>
              Период: ${periodText}<br>
              Количество отчетов: ${selectedReportsData.length}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Дата</th>
                <th>Имя</th>
                <th>Сумма</th>
                <th>Кол-во Юк</th>
                <th>Детали</th>
              </tr>
            </thead>
            <tbody>
              ${selectedReportsData.map((report, index) => {
                const detailsHtml = report.details
                  .filter((detail) => {
                    const totalQuantity = detail.dayQuantity + detail.nightQuantity - detail.wasteQuantity;
                    return totalQuantity > 0;
                  })
                  .map((detail, detailIndex) => {
                    const totalQuantity = detail.dayQuantity + detail.nightQuantity - detail.wasteQuantity;
                    return `${detailIndex + 1}. ${detail.forName} Кол-во ${Math.round(totalQuantity)}`;
                  })
                  .join('<br>');

                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${report.date}</td>
                    <td>${report.workerName}</td>
                    <td>${Math.round(report.totalPrice).toLocaleString()}</td>
                    <td>${Math.round(report.yuk)}</td>
                    <td>
                      <div class="details">${detailsHtml}</div>
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div class="summary">
            <h3>📊 Суммарно</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <strong>Общая сумма:</strong>
                <div style="font-size: 16px; color: #1890ff; font-weight: bold; margin-top: 5px;">
                  ${Math.round(totalAmount).toLocaleString()}
                </div>
              </div>
              <div class="summary-item">
                <strong>Общее количество Юк:</strong>
                <div style="font-size: 16px; color: #52c41a; font-weight: bold; margin-top: 5px;">
                  ${Math.round(totalYuk)}
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  useEffect(() => {
    fetchReports();
  }, [page, pageSize, dateRange]);

  const rowSelection = {
    selectedRowKeys: selectedReports,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedReports(selectedRowKeys as string[]);
    },
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Имя",
      dataIndex: "workerName",
      key: "workerName",
    },
    {
      title: "Сумма",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (totalPrice: number) => (
        <span>{Math.round(totalPrice).toLocaleString()}</span>
      ),
    },
    {
      title: "Кол-во Юк",
      dataIndex: "yuk",
      key: "yuk",
      render: (yuk: number) => (
        <span>{Math.round(yuk)}</span>
      ),
    },
    {
      title: "Детали",
      key: "details",
      render: (_: any, record: SalaryReport) => {
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              maxWidth: "900px",
              margin: 0,
              padding: 0,
            }}
          >
            {record.details
              .filter((detail) => {
                const totalQuantity = detail.dayQuantity + detail.nightQuantity - detail.wasteQuantity;
                return totalQuantity > 0;
              })
              .map((detail, index) => {
                const totalQuantity = detail.dayQuantity + detail.nightQuantity - detail.wasteQuantity;
                return (
                  <p key={index}>
                    {index + 1}.{detail.forName} Кол-во{" "}
                    {Math.round(totalQuantity)}
                  </p>
                );
              })}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, report: SalaryReport) => {
        const menu = (
          <Menu>
            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Accountant) && (
              <Popconfirm
                title="Вы уверены, что хотите удалить этот отчет ?"
                okText="Да"
                cancelText="Нет"
                onConfirm={() => handleDeleteReport(report._id)}
              >
                <Menu.Item>Удалить</Menu.Item>
              </Popconfirm>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Отчеты {name}</Title>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          icon={<PrinterOutlined />}
          onClick={handlePrintReports}
          disabled={selectedReports.length === 0}
        >
          Печать ({selectedReports.length})
        </Button>
      </Space>

      <Table
        loading={loading}
        columns={columns}
        dataSource={reports}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        rowSelection={rowSelection}
      />
    </div>
  );
}
